import Spinner from "../shared/Spinner";
import {OverlayTrigger, Tooltip} from "react-bootstrap";
import React, {useState, useEffect} from "react";
import {seoService} from "../../services/seoService.js";
import cogoToast from "cogo-toast";


const showError = (message, e) => {
    let errorMessage = message + ": " + (e?.error?.message || e?.message || "unknown_error");
    if (e?.validationErrors) {
        errorMessage += "\n";
        e.validationErrors.forEach((error) => {
            errorMessage += error.param + ": " + error.msg + "\n";
        })
    }
    cogoToast.error(errorMessage, {
        hideAfter: 5,
        position: 'top-center',
        heading: 'Error'
    })
}

const EditAffiliateConfig = ({affiliate, affiliateConfig, setAffiliateConfig, newAffiliate}) => {

    let currentConfig = {...affiliateConfig};

    const handleChange = (name, e) => {
        let inputValue = e.target?.value || 0;
        let newValue = name === "affiliate" ? inputValue : inputValue / 100;
        let newConfig = {...currentConfig, [name]: newValue};
        setAffiliateConfig(newConfig);
        currentConfig = newConfig;
    }

    return (
        <form className={"edit-form"}>
            <div className={"form-group mb-2"}>
                {affiliate && <h1>{affiliate}</h1>}
                {newAffiliate && <>
                    <label htmlFor={"affiliate"}>Affiliate</label>
                    <small>Must match exactly the <b>affiliate</b> parameter sent on the booking link.</small>
                    <input type="text" className="form-control" name={"affiliate"} onChange={(e) => handleChange("affiliate", e)}/>
                    <br/>
                </>}
                <label htmlFor={"ratio"}>Transfer Rate %</label>
                <small>Percentage of the booking fee transferred to the affiliate.</small>
                <input type="number" className="form-control" name={"ratio"} defaultValue={affiliateConfig.ratio * 100} onChange={(e) => handleChange("ratio", e)}/>
            </div>
        </form>)
}

const Affiliates = ({setModal}) => {

    const [loading, setLoading] = useState(true);
    const [affiliates, setAffiliates] = useState({});
    const [defaultRatio, setDefaultRatio] = useState(0);

    useEffect(() => {
        seoService.getAffiliateConfig().then((res) => {
            setAffiliates(res?.affiliates || {});
            setDefaultRatio(res?.default_ratio || 0);
        }).catch((e) => {
            console.error("Error getting affiliate config", e);
        }).finally(() => {
            setLoading(false);
        })
    }, []);

    const handleAddNew = () => {
        let affiliateConfig = {};
        const setAffiliateConfig = (newConfig) => {
            affiliateConfig = newConfig;
        }
        setModal({
            title: "New Affiliate",
            size: "md",
            message: <EditAffiliateConfig affiliateConfig={affiliateConfig} setAffiliateConfig={setAffiliateConfig} newAffiliate/>,
            onSubmit: async () => {
                try {
                    let res = await seoService.createAffiliateConfig(affiliateConfig.affiliate, affiliateConfig.ratio);
                    setAffiliates(res.affiliates);
                    cogoToast.success(affiliateConfig.affiliate + ' created successfully!', {
                        hideAfter: 5,
                        position: 'top-center',
                        heading: 'Success'
                    })
                    setModal(m => ({...m, hide: true}));
                } catch (e) {
                    showError("Error creating affiliate", e);
                }
            }
        });
    }

    const handleEdit = (affiliate, currentConfig) => {
        let affiliateConfig = {...currentConfig};
        const setAffiliateConfig = (newConfig) => {
            affiliateConfig = newConfig;
        }
        setModal({
            title: "Edit Affiliate Transfer Rate",
            size: "md",
            message: <EditAffiliateConfig affiliate={affiliate} affiliateConfig={affiliateConfig} setAffiliateConfig={setAffiliateConfig}/>,
            onSubmit: async () => {
                try {
                    let res = await seoService.updateAffiliateConfig(affiliate, affiliateConfig.ratio);
                    setAffiliates(res.affiliates);
                    cogoToast.success('Affiliate updated successfully!', {
                        hideAfter: 5,
                        position: 'top-center',
                        heading: 'Success'
                    })
                    setModal(m => ({...m, hide: true}));
                } catch (e) {
                    showError("Error editing affiliate", e);
                }
            }
        });
    }

    const handleDelete = (affiliate) => {
        setModal({
            title: "Delete Affiliate",
            size: "md",
            message: <div>Are you sure you want to delete <b>{affiliate}</b>? It will fall back to the default rate.</div>,
            onSubmit: async () => {
                try {
                    let res = await seoService.deleteAffiliateConfig(affiliate);
                    setAffiliates(res.affiliates);
                    setModal(m => ({...m, hide: true}));
                } catch (e) {
                    showError("Error deleting affiliate", e);
                }
            }
        });
    }

    //The default row always shows up, even before it is explicitly saved on the config
    const rows = [
        {affiliate: "default", config: affiliates["default"] || {ratio: defaultRatio}, isDefault: true},
        ...Object.keys(affiliates).filter(a => a !== "default").sort().map(a => ({affiliate: a, config: affiliates[a]}))
    ];

    return (
        <div className="card">
            <div className="card-body" style={{overflowX: 'auto'}}>
                <button className={"btn btn-success mb-2"} onClick={handleAddNew}><i className={"mdi mdi-plus"}/>Add Affiliate</button>
                {loading ? <Spinner inline/> : <>
                    <small>Rate applied on the /external/affiliateData report. Changing a rate also changes past periods.</small>
                    <table className={"table border"}>
                        <thead>
                        <tr>
                            <th>Affiliate</th>
                            <th style={{textAlign: "center"}}>Transfer Rate</th>
                            <th style={{textAlign: "center"}}></th>
                        </tr>
                        </thead>
                        <tbody>
                        {rows.map(({affiliate, config, isDefault}) => {
                            return (<tr key={affiliate}>
                                <td>{affiliate}{isDefault && !affiliates["default"] ? <small> (not set, using fallback)</small> : null}</td>
                                <td style={{textAlign: "center"}}>{Math.round(config.ratio * 10000) / 100}%</td>
                                <td style={{textAlign: "center"}}>
                                    <div className={"d-flex justify-content-end table_actions"}>
                                        <OverlayTrigger overlay={<Tooltip>Edit</Tooltip>}>
                                            <i className={"mdi mdi-pencil"} onClick={() => handleEdit(affiliate, config)}></i>
                                        </OverlayTrigger>
                                        {!isDefault && <OverlayTrigger overlay={<Tooltip>Delete</Tooltip>}>
                                            <i className={"mdi mdi-trash-can"} onClick={() => handleDelete(affiliate)}></i>
                                        </OverlayTrigger>}
                                    </div>
                                </td>
                            </tr>)
                        })}
                        </tbody>
                    </table>
                </>}
                <div></div>
            </div>
        </div>
    )
}

export default Affiliates;
